import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Loader2, Copy, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SelfScheduleLinkModal — admin picks a round, optionally edits the message,
 * sends a self-scheduling link to the applicant. After send, shows the URL
 * so the admin can copy it in case they want to message it elsewhere.
 */
export default function SelfScheduleLinkModal({ open, onClose, applicant }) {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(14);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSentResult(null);
    setSending(false);
    setLoading(true);
    const jobPostingId = applicant?.job_posting_id || applicant?.position_id;
    axios.get(`${API}/admin/schedule-templates/effective`, {
      params: jobPostingId ? { job_posting_id: jobPostingId } : {},
    }).then(res => {
      setTemplate(res.data);
      const rounds = res.data?.rounds || [];
      setSelectedRound(rounds[0]?.name || '');
    }).catch(e => {
      toast.error(`Failed to load template: ${e.response?.data?.detail || e.message}`);
    }).finally(() => setLoading(false));
  }, [open, applicant]);

  if (!open) return null;

  const rounds = template?.rounds || [];
  const round = rounds.find(r => r.name === selectedRound);
  const interviewerCount = round?.interviewer_user_ids?.length || 0;

  const handleSend = async () => {
    if (!applicant?.id) return;
    if (!selectedRound) { toast.error('Pick a round first'); return; }
    if (interviewerCount === 0) {
      toast.error('This round has no eligible interviewers. Update the schedule template first.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/applicants/${applicant.id}/send-schedule-link`, {
        round_name: selectedRound,
        expires_in_days: expiresInDays,
        custom_message: customMessage.trim() || null,
      });
      setSentResult(res.data);
      if (res.data?.email_sent) {
        toast.success(`Link sent to ${applicant.email}`);
      } else {
        toast.warning(`Link created but email failed: ${res.data?.email_error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleCopyUrl = () => {
    if (sentResult?.url) {
      navigator.clipboard.writeText(sentResult.url);
      toast.success('Link copied');
    }
  };

  const synthesizedWarning = template?.synthesized && !sentResult;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="self-schedule-link-modal">
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h2 className="text-base font-semibold inline-flex items-center gap-2">
            <Send size={16} className="text-primary" />
            Send self-schedule link
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="self-schedule-close-btn">
            <X size={18} />
          </Button>
        </header>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="animate-spin mr-2" /> Loading template…
            </div>
          ) : sentResult ? (
            <div className="space-y-3" data-testid="self-schedule-sent">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900">
                {sentResult.email_sent
                  ? `Email sent to ${applicant?.email}. They'll get a link to pick a time.`
                  : `Link created — email delivery failed. Copy it below and send it manually.`}
              </div>
              <div className="flex items-center gap-2">
                <Input value={sentResult.url || ''} readOnly className="flex-1 text-xs font-mono" />
                <Button size="sm" variant="outline" onClick={handleCopyUrl} title="Copy">
                  <Copy size={14} />
                </Button>
                {sentResult.url && (
                  <Button size="sm" variant="outline" asChild title="Open">
                    <a href={sentResult.url} target="_blank" rel="noreferrer">
                      <ExternalLink size={14} />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              {synthesizedWarning && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  No schedule template saved yet. Showing defaults — be sure to assign at least one interviewer in
                  {' '}<a href="/admin/schedule-templates" target="_blank" rel="noreferrer" className="underline font-medium">Schedule Templates</a>.
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Recipient</label>
                <Input value={applicant?.email || '(no email)'} readOnly className="mt-1" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Round</label>
                <select
                  value={selectedRound}
                  onChange={e => setSelectedRound(e.target.value)}
                  className="w-full h-9 px-3 text-sm border rounded-md bg-background mt-1"
                  data-testid="self-schedule-round-select"
                >
                  {rounds.length === 0 && <option value="">(no rounds configured)</option>}
                  {rounds.map(r => (
                    <option key={r.name} value={r.name}>
                      {r.name} — {r.duration_minutes}m · {r.interview_type}
                    </option>
                  ))}
                </select>
                {round && (
                  <p className={`text-xs mt-1 ${interviewerCount === 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                    {interviewerCount === 0
                      ? '⚠ No interviewers eligible for this round. Update the template first.'
                      : `${interviewerCount} interviewer${interviewerCount !== 1 ? 's' : ''} eligible`}
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Link expires in (days)</label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={expiresInDays}
                  onChange={e => setExpiresInDays(parseInt(e.target.value, 10) || 14)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Custom message (optional)</label>
                <Textarea
                  value={customMessage}
                  onChange={e => setCustomMessage(e.target.value)}
                  placeholder="Leave blank to use the default friendly message"
                  className="mt-1 min-h-[80px]"
                />
              </div>
            </>
          )}
        </div>

        <footer className="px-4 py-3 border-t bg-muted/30 flex justify-end gap-2">
          {sentResult ? (
            <Button onClick={onClose} data-testid="self-schedule-done-btn">Done</Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>Cancel</Button>
              <Button
                onClick={handleSend}
                disabled={sending || loading || !applicant?.email || interviewerCount === 0}
                className="gap-2"
                data-testid="self-schedule-send-btn"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {sending ? 'Sending…' : 'Send link'}
              </Button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
